import React, { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import ProfileForm from './ProfileForm';

const MAX_CHILDREN = 7;

const ProfileDetailsStep = ({ formData, handleInputChange, prevStep, handleSubmit, errors, loading }) => {
  const [agreement, setAgreement] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleProfileChange = (e, index = null) => {
    const { name, value } = e.target;
    
    if (formData.accountType === 'self') {
      handleInputChange({
        target: {
          name: 'profile',
          value: { ...formData.profile, [name]: value }
        }
      });
    } else if (index !== null) {
      const updatedChildren = [...formData.children];
      updatedChildren[index] = { ...updatedChildren[index], [name]: value };
      handleInputChange({
        target: {
          name: 'children',
          value: updatedChildren
        }
      });
    }
  };

  const addChild = () => {
    if (formData.children.length >= MAX_CHILDREN) {
      return; // Don't add more if limit reached
    }

    handleInputChange({
      target: {
        name: 'children',
        value: [...formData.children, {
          firstName: '',
          lastName: '',
          dateOfBirth: '',
          gender: '',
          genderOther: '',
          ethnicity: '',
          ethnicityOther: '',
          disability: '',
          disabilityOther: '',
          interests: [],
          educationStatus: '',
          ehcp: '',
          dla: '',
          pip: ''
        }]
      }
    });
  };

  const removeChild = (index) => {
    const updatedChildren = formData.children.filter((_, i) => i !== index);
    handleInputChange({
      target: {
        name: 'children',
        value: updatedChildren
      }
    });
  };

  const validateStep = () => {
    const stepErrors = {};

    if (!agreement) {
      stepErrors.agreement = 'You must agree to the terms and conditions';
    }

    if (formData.accountType === 'children' && formData.children.length === 0) {
      stepErrors.children = 'Please add at least one child profile';
      return stepErrors;
    }

    const validateProfile = (profile, prefix = '') => {
      if (!profile.dateOfBirth) stepErrors[`${prefix}dateOfBirth`] = 'Date of birth is required';
      if (!profile.gender) stepErrors[`${prefix}gender`] = 'Gender is required';
      if (profile.gender === 'other' && !profile.genderOther?.trim()) {
        stepErrors[`${prefix}genderOther`] = 'Please specify gender';
      }
      if (!profile.ethnicity) stepErrors[`${prefix}ethnicity`] = 'Ethnicity is required';
      if (profile.ethnicity === 'Other' && !profile.ethnicityOther?.trim()) {
        stepErrors[`${prefix}ethnicityOther`] = 'Please specify ethnicity';
      }
      if (!profile.disability) stepErrors[`${prefix}disability`] = 'Disability is required';
      if (profile.disability === 'Other' && !profile.disabilityOther?.trim()) {
        stepErrors[`${prefix}disabilityOther`] = 'Please specify disability';
      }
      if (!profile.educationStatus) stepErrors[`${prefix}educationStatus`] = 'Education status is required';
      if (!profile.ehcp) stepErrors[`${prefix}ehcp`] = 'EHCP status is required';
      if (!profile.dla) stepErrors[`${prefix}dla`] = 'DLA status is required';
      if (!profile.pip) stepErrors[`${prefix}pip`] = 'PIP status is required';
      if (!profile.interests?.length) stepErrors[`${prefix}interests`] = 'Please select at least one interest';
    };

    if (formData.accountType === 'self') {
      validateProfile(formData.profile);
    } else {
      // Only validate if there are children
      if (formData.children.length > 0) {
        formData.children.forEach((child, index) => {
          if (!child.firstName?.trim()) stepErrors[`child${index}firstName`] = 'First name is required';
          if (!child.lastName?.trim()) stepErrors[`child${index}lastName`] = 'Last name is required';
          validateProfile(child, `child${index}`);
        });
      }
    }

    return stepErrors;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const stepErrors = validateStep();
    
    if (Object.keys(stepErrors).length === 0) {
      handleSubmit(e);
    } else {
      handleInputChange({ target: { name: 'errors', value: stepErrors } });
    }
  };

  const getChildErrors = (index) => {
    const childErrors = {};
    Object.keys(errors || {}).forEach(key => {
      if (key.startsWith(`child${index}`)) {
        childErrors[key.replace(`child${index}`, '')] = errors[key];
      }
    });
    return childErrors;
  };

  const handlePrevStep = () => {
    prevStep();
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 text-center mb-6">
        {formData.accountType === 'self' ? 'Your Profile Details' : 'Children Profiles'}
      </h3>

      {formData.accountType === 'self' ? (
        <ProfileForm
          profile={formData.profile}
          onChange={handleProfileChange}
          errors={errors}
          isChild={false}
        />
      ) : (
        <div className="space-y-6">
          {formData.children.map((child, index) => (
            <div key={index} className="relative">
              <ProfileForm
                profile={child}
                onChange={(e) => handleProfileChange(e, index)}
                onRemove={() => removeChild(index)}
                index={index}
                errors={getChildErrors(index)}
                isChild={true}
              />
            </div>
          ))}
          
          {formData.children.length < MAX_CHILDREN ? (
            <>
              {errors.children && (
                <p className="text-center text-xs text-red-600 mb-2">
                  {errors.children}
                </p>
              )}
              <button
                type="button"
                onClick={addChild}
                className="w-full flex items-center justify-center py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-blue-500 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Another Child
              </button>
            </>
          ) : (
            <p className="text-center text-xs text-gray-500">
              Maximum number of children ({MAX_CHILDREN}) reached
            </p>
          )}
        </div>
      )}

      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="terms"
            type="checkbox"
            className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={agreement}
            onChange={(e) => setAgreement(e.target.checked)}
          />
        </div>
        <div className="ml-2">
          <label htmlFor="terms" className="text-xs text-gray-700">
            I agree to the{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Terms and Conditions
            </a>
            {' '}and{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Privacy Policy
            </a>
          </label>
        </div>
      </div>
      {errors.agreement && <p className="text-xs text-red-600">{errors.agreement}</p>}

      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={handlePrevStep}
          className="py-1.5 px-3 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={loading}
          className={`py-1.5 px-6 border border-transparent rounded-md shadow-sm text-xs font-medium text-white ${
            loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </div>
    </form>
  );
};

export default ProfileDetailsStep; 